import { z } from 'zod';
import { FactSource } from './fact-provenance.js';
/**
 * FactConflict — the wire shape the dossier conflict-resolver renders
 * when two or more sources disagree on the same fact key (e.g. ARR
 * reported as $4.2M in Salesforce but $4.8M in a voice note last week).
 * The CEO sees a side-by-side of all candidates, picks one (or
 * overrides with a free-text value), and optionally tags a policy
 * preference ("when sources disagree on this kind of fact, prefer
 * voice notes") that the conflict layer will apply next time.
 *
 * Persisted at `organizations/{orgId}/factConflicts/{id}`; created by
 * the synthesis layer when it spots disagreement, resolved by
 * `POST /api/orgs/:o/dossier/:dossierId/conflict/resolve` (issue #166
 * PR A — dossier extras).
 */
/**
 * One candidate value carried in a conflict. Each candidate carries
 * its source, the value as-written, when the agent saw it, the trust
 * score the synthesis layer assigned, and an optional source-side note
 * (e.g. "from Q1 board deck").
 */
export const ConflictCandidate = z.object({
    source: FactSource,
    value: z.string(),
    capturedAt: z.string().datetime(),
    trustScore: z.number().min(0).max(1),
    note: z.string().optional(),
});
/**
 * Source-resolution policy — the CEO's stored preference for which
 * source wins on this kind of conflict next time. Used by the
 * conflict-resolver "remember my choice" affordance.
 *
 *   voice-notes-first: prefer the CEO's own voice memos
 *   salesforce-first:  prefer the CRM canonical
 *   always-ask:        don't auto-resolve; surface the conflict every time
 */
export const ConflictResolutionPolicy = z.enum([
    'voice-notes-first',
    'salesforce-first',
    'always-ask',
]);
/**
 * The CEO's resolution. `kind: 'pick'` selects one of the candidates'
 * `value`s verbatim; `kind: 'override'` carries a free-text value the
 * CEO typed that didn't match any candidate. `sourcePolicy` is optional
 * — present when the CEO ticked "remember this choice for next time".
 */
export const ConflictResolution = z.object({
    kind: z.enum(['pick', 'override']),
    value: z.string(),
    sourcePolicy: ConflictResolutionPolicy.optional(),
    resolvedAt: z.string().datetime(),
    resolvedByUid: z.string(),
});
export const FactConflict = z.object({
    id: z.string(),
    orgId: z.string(),
    /** Which dossier the fact belongs to (so the resolver can route the patch). */
    dossierId: z.string(),
    /** Stable key for the disputed fact — e.g. "arr", "headcount", "ceo-name". */
    factKey: z.string(),
    /** At least two candidates — a single-candidate "conflict" doesn't exist. */
    candidates: z.array(ConflictCandidate).min(2),
    /** Undefined while the conflict is open. */
    userResolution: ConflictResolution.optional(),
    createdAt: z.string().datetime(),
});
